import * as React from "react";
import { capture } from "@/lib/posthog";

/**
 * Report a role-gate denial exactly once per denial.
 *
 * This lives outside App.tsx for one reason: App.tsx cannot be imported in a
 * test (it loads Keycloak at module scope), and the property that matters here
 * is behavioural rather than structural. A test that only proves the call sits
 * inside an effect proves nothing about how many events a denial produces.
 *
 * Why a ref and not just an effect. `permission_denied_shown` is the only
 * signal that catches a frontend gate disagreeing with the backend -- the user
 * just bounces, and nothing else records it -- so its alert threshold is zero
 * and the count has to be exact. Moving the call out of the render body was not
 * enough: React 18 StrictMode mounts, runs effects, runs cleanup and runs them
 * again on the same instance in development, so one denial still reported
 * twice. The ref survives that, because the instance does.
 *
 * Keyed on the denial tuple rather than a boolean: a gate that denies, is
 * satisfied, then denies again for a different role or route is a second real
 * denial and should report. Cleared when the denial clears.
 */
export function useDenialReport(
  denied: boolean,
  requiredRole: string | null,
  actualRole: string | null,
): void {
  const reported = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!denied) {
      reported.current = null;
      return;
    }
    const route = typeof window === "undefined" ? "" : window.location.pathname;
    const key = `${requiredRole ?? ""}|${actualRole ?? ""}|${route}`;
    if (reported.current === key) return;
    reported.current = key;
    capture("permission_denied_shown", {
      required_role: requiredRole,
      actual_role: actualRole,
      route,
    });
  }, [denied, requiredRole, actualRole]);
}
